import React, { useMemo, useState } from "react";

import {
  Grid,
  IconButton,
  List,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";

/**
 * FilteredList allows us to filter the elements of a list based on part of their name.
 */
export function FilteredList<ItemType>({
  data,
  ListItemComponent,
  getKey,
  getName,
  onItemSelect,
  isLoading = false,
}: FilteredListProps<ItemType>) {
  const [currentSearch, setCurrentSearch] = useState<string>("");

  const displayedData = useMemo(() => {
    if (!currentSearch) return data;
    const regex = new RegExp(currentSearch, "i");
    return data.filter((item) => regex.test(getName(item)));
  }, [currentSearch, data, getName]);

  const handleSearchString = ({ target: { value } }) => {
    setCurrentSearch(value);
  };

  const handleSearchCloseButton = () => {
    if (currentSearch) {
      setCurrentSearch("");
    }
  };

  return (
    <Grid container item direction="column">
      <TextField
        onChange={handleSearchString}
        sx={{ mb: 2 }}
        InputProps={{
          endAdornment: (
            <IconButton onClick={handleSearchCloseButton}>
              {currentSearch ? <Close /> : <Search />}
            </IconButton>
          ),
        }}
      />
      {isLoading && <Skeleton height="200px" />}
      {!isLoading && data.length !== 0 && (
        <List>
          {displayedData.map((item) => (
            <ListItemComponent
              key={getKey(item)}
              item={item}
              onItemSelect={onItemSelect}
            />
          ))}
        </List>
      )}
      {!isLoading && data?.length === 0 && (
        <>
          <Typography variant="body2" align="center">
            Você não possui ambientes qualificados para criação de eventos
            automatizados
          </Typography>
          <Typography align="center">
            Um ambiente precisa ter pelo menos um dispositivo online, e nenhum
            dispositivo offline para receber um evento.
          </Typography>
        </>
      )}
    </Grid>
  );
}
export interface IFilteredListLIProps<ItemType> {
  item: ItemType;
  onItemSelect: FilteredListProps<ItemType>["onItemSelect"];
}

export interface FilteredListProps<ItemType> {
  /** The list of items to show. */
  data: Array<ItemType>;
  /** The component to render each item.Should use MUI's ListItem as base */
  ListItemComponent: React.ComponentType<IFilteredListLIProps<ItemType>>;
  /** A function to get the key of an item. */
  getKey: (item: ItemType) => string | number;
  /** A function to get the name of an item. */
  getName: (item: ItemType) => string;
  /** A function to handle the selection of an item. */
  onItemSelect?: (item: ItemType, selected: boolean) => void;
  /** Display a loading skeleton, defaults to false */
  isLoading?: boolean;
}
